import importExportApi from '@/api/importExportApi'
import fileSaver from 'file-saver'

// Helper: trigger a Blob download in the browser
function triggerBlobDownload (blob, filename) {
  fileSaver.saveAs(blob, filename)
}

/**
 * Extract the filename from an axios response's Content-Disposition header.
 * Falls back to `fallback` when the header is absent or cannot be parsed.
 * Handles both quoted and unquoted filename values:
 *   Content-Disposition: attachment; filename="My_Company_properties_export_02_26_2026.xlsx"
 *   Content-Disposition: attachment; filename=My_Company_properties_export_02_26_2026.xlsx
 */
function getFilenameFromResponse (response, fallback) {
  try {
    const cd = response.headers && response.headers['content-disposition']
    if (cd) {
      const match = cd.match(/filename[^;=\n]*=\s*(?:UTF-8'')?(['"]?)([^;\n\r"']+)\1/i)
      if (match && match[2]) return decodeURIComponent(match[2].trim())
    }
  } catch (_) {}
  return fallback
}

/**
 * Build a filename consistent with the API convention:
 *   {Company}_{EntityType}_{suffix}_{MM_dd_yyyy}.xlsx
 * Used as the client-side fallback when Content-Disposition is unavailable.
 */
function buildFilename (companyName, entityType, suffix) {
  const sanitize = s => (s || '').replace(/[^\w\s-]/g, '').trim().replace(/\s+/g, '_')
  const now = new Date()
  const mm = String(now.getMonth() + 1).padStart(2, '0')
  const dd = String(now.getDate()).padStart(2, '0')
  const yyyy = now.getFullYear()
  const companyPart = companyName ? `${sanitize(companyName)}_` : ''
  return `${companyPart}${sanitize(entityType)}_${suffix}_${mm}_${dd}_${yyyy}.xlsx`
}

// Helper: trigger download via a pre-signed URL
function triggerUrlDownload (url) {
  const link = document.createElement('a')
  link.href = url
  link.target = '_blank'
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
}

export default {
  namespaced: true,

  state: () => {
    // Rehydrate active jobs from localStorage (survive page reload)
    let activeJobs = {}
    try {
      const saved = localStorage.getItem('importExportActiveJobs')
      if (saved) activeJobs = JSON.parse(saved)
    } catch (_) {}

    return {
      // Phase 1
      templateDownloading: false,
      exportDownloading: false,
      allExportsDownloading: false,
      downloadError: null,

      // Phase 2 (stubs — filled in later phases)
      uploadedFile: null,
      validationResult: null,
      validating: false,
      validationError: null,

      // Phase 3
      importing: false,
      importResult: null,
      importError: null,
      importMode: 'upsert',    // 'upsert' | 'appendonly' | 'replace'

      // Phase 4+ — async jobs (stubs)
      activeJobs,  // { [jobId]: { jobId, jobType, status, pollUrl, downloadUrl, fileSizeBytes, expiresAtUtc, error, pollingIntervalId } }

      // Phase 5 — async job UI + events count preview
      isPollingJob: false,
      eventsCount: null,
      eventsCountLoading: false,

      // Phase 6 — re-auth required flag (set when a 401 is received during download)
      requiresReAuth: false,

      // Phase 7 — property files download + company archive
      selectedPropertyId: null,
      propertyFileOptions: { preserveFolderStructure: true, includeArchived: false },
      propertyFilesDownloading: false,
      archiveEstimate: null,
      archiveOptions: {
        includeCompanyFiles: true,
        includePropertyFiles: true,
        includeEventData: true,
        includeInsuranceData: true,
        includeProcedures: true,
        includeIncidentReports: true
      },
      archiveEstimateLoading: false,
      archiveJobSubmitting: false,

      // AI Policy Extraction
      aiExtractFile: null,
      aiExtracting: false,
      aiExtractionResult: null,
      aiExtractionError: null,

      // Phase 9 — Recent export jobs panel
      myJobsLoading: false
    }
  },

  mutations: {
    SET_TEMPLATE_DOWNLOADING (state, val) { state.templateDownloading = val },
    SET_EXPORT_DOWNLOADING (state, val) { state.exportDownloading = val },
    SET_ALL_EXPORTS_DOWNLOADING (state, val) { state.allExportsDownloading = val },
    SET_DOWNLOAD_ERROR (state, err) { state.downloadError = err },

    // Phase 2
    SET_UPLOADED_FILE (state, file) { state.uploadedFile = file },
    SET_VALIDATION_RESULT (state, result) { state.validationResult = result },
    SET_VALIDATING (state, val) { state.validating = val },
    SET_VALIDATION_ERROR (state, err) { state.validationError = err },
    CLEAR_VALIDATION (state) {
      state.validationResult = null
      state.validationError = null
      state.uploadedFile = null
      state.importResult = null
      state.importError = null
    },

    // Phase 3
    SET_IMPORTING (state, val) { state.importing = val },
    SET_IMPORT_RESULT (state, result) { state.importResult = result },
    SET_IMPORT_ERROR (state, err) { state.importError = err },
    SET_IMPORT_MODE (state, mode) { state.importMode = mode },
    CLEAR_IMPORT (state) {
      state.importResult = null
      state.importError = null
    },

    // Phase 4+ — async jobs
    SET_ACTIVE_JOB (state, job) {
      state.activeJobs = { ...state.activeJobs, [job.jobId]: job }
      try { localStorage.setItem('importExportActiveJobs', JSON.stringify(state.activeJobs)) } catch (_) {}
    },
    UPDATE_JOB_STATUS (state, { jobId, status, downloadUrl, fileSizeBytes, expiresAtUtc, error }) {
      if (state.activeJobs[jobId]) {
        state.activeJobs = {
          ...state.activeJobs,
          [jobId]: { ...state.activeJobs[jobId], status, downloadUrl, fileSizeBytes, expiresAtUtc, error }
        }
        try { localStorage.setItem('importExportActiveJobs', JSON.stringify(state.activeJobs)) } catch (_) {}
      }
    },
    SET_JOB_POLLING_INTERVAL (state, { jobId, intervalId }) {
      if (state.activeJobs[jobId]) {
        state.activeJobs = {
          ...state.activeJobs,
          [jobId]: { ...state.activeJobs[jobId], pollingIntervalId: intervalId }
        }
      }
    },
    REMOVE_ACTIVE_JOB (state, jobId) {
      const jobs = { ...state.activeJobs }
      delete jobs[jobId]
      state.activeJobs = jobs
    },

    // Phase 5 — async job flags + events count
    SET_IS_POLLING_JOB (state, val) { state.isPollingJob = val },
    SET_EVENTS_COUNT (state, count) { state.eventsCount = count },
    SET_EVENTS_COUNT_LOADING (state, val) { state.eventsCountLoading = val },

    // Phase 6 — re-auth flag
    SET_REQUIRES_REAUTH (state, val) { state.requiresReAuth = val },

    // Phase 7 — property files + company archive
    SET_SELECTED_PROPERTY_ID (state, id) { state.selectedPropertyId = id },
    SET_PROPERTY_FILE_OPTIONS (state, opts) { state.propertyFileOptions = { ...state.propertyFileOptions, ...opts } },
    SET_PROPERTY_FILES_DOWNLOADING (state, val) { state.propertyFilesDownloading = val },
    SET_ARCHIVE_ESTIMATE (state, estimate) { state.archiveEstimate = estimate },
    SET_ARCHIVE_OPTIONS (state, opts) { state.archiveOptions = { ...state.archiveOptions, ...opts } },
    SET_ARCHIVE_ESTIMATE_LOADING (state, val) { state.archiveEstimateLoading = val },
    SET_ARCHIVE_JOB_SUBMITTING (state, val) { state.archiveJobSubmitting = val },

    // Phase 9 — Recent jobs
    SET_MY_JOBS_LOADING (state, val) { state.myJobsLoading = val },

    // AI Policy Extraction
    SET_AI_EXTRACT_FILE (state, file) { state.aiExtractFile = file },
    SET_AI_EXTRACTING (state, val) { state.aiExtracting = val },
    SET_AI_EXTRACTION_RESULT (state, result) { state.aiExtractionResult = result },
    SET_AI_EXTRACTION_ERROR (state, err) { state.aiExtractionError = err },
    CLEAR_AI_EXTRACTION (state) {
      state.aiExtractFile = null
      state.aiExtracting = false
      state.aiExtractionResult = null
      state.aiExtractionError = null
    },

    // Phase 4 — reset file/validation/import state when navigating away
    RESET_STATE (state) {
      // Clear any active polling intervals before wiping state so we don't leak setInterval handles.
      // We clear the intervalId from each job but keep the job entries — loadMyExportJobs will
      // reconcile with the server and restart polling when the user returns to the export pages.
      const clearedJobs = {}
      Object.entries(state.activeJobs).forEach(([id, job]) => {
        if (job.pollingIntervalId) clearInterval(job.pollingIntervalId)
        clearedJobs[id] = { ...job, pollingIntervalId: null }
      })
      state.activeJobs = clearedJobs
      state.isPollingJob = false
      try { localStorage.setItem('importExportActiveJobs', JSON.stringify(clearedJobs)) } catch (_) {}
      state.uploadedFile = null
      state.validationResult = null
      state.validating = false
      state.validationError = null
      state.importing = false
      state.importResult = null
      state.importError = null
      state.importMode = 'upsert'
      state.downloadError = null
      state.requiresReAuth = false
      // Phase 7
      state.selectedPropertyId = null
      state.propertyFilesDownloading = false
      state.archiveEstimate = null
      state.archiveEstimateLoading = false
      state.archiveJobSubmitting = false
      // AI Extraction
      state.aiExtractFile = null
      state.aiExtracting = false
      state.aiExtractionResult = null
      state.aiExtractionError = null
    }
  },

  getters: {
    /** True when the validation result has errors (import blocked). */
    hasErrors: state => !!(state.validationResult && !state.validationResult.isValid),

    /** True when the validation result has at least one warning row. */
    hasWarnings: state => !!(state.validationResult && (state.validationResult.warningRowCount || 0) > 0),

    /** True when a valid validation result exists and no import is running. */
    canImport: state => !!(state.validationResult && state.validationResult.isValid && !state.importing),

    /** True when any warning has errorCode 'PEOPLE_EMAIL_CHANGE'. */
    hasEmailChangeWarnings: state => !!(state.validationResult &&
      (state.validationResult.warnings || []).some(w => w.errorCode === 'PEOPLE_EMAIL_CHANGE')),

    isJobComplete: state => jobId => {
      const job = state.activeJobs[jobId]
      return job && job.status === 'Complete'
    },

    /**
     * True when the current user (this browser session) already has a NotStarted or InProgress
     * job for the given company. Prevents submitting a second concurrent export.
     * Scoped to companyId — activeJobs only ever contains the logged-in user's own jobs.
     */
    hasAnyActiveJob: state => companyId => {
      if (!companyId) return false
      return Object.values(state.activeJobs).some(j =>
        j.companyId === companyId &&
        (j.status === 'NotStarted' || j.status === 'InProgress')
      )
    },
    isJobFailed: state => jobId => {
      const job = state.activeJobs[jobId]
      return job && job.status === 'Error'
    },
    jobDownloadUrl: state => jobId => {
      const job = state.activeJobs[jobId]
      return job ? job.downloadUrl : null
    },

    // Phase 7 — archive helpers
    hasArchiveEstimate: state => state.archiveEstimate !== null,
    archiveIsLarge: state => !!(state.archiveEstimate && state.archiveEstimate.isLargeArchive === true)
  },

  actions: {
    // ─────────────────────────────────────────────────────────────────────────
    // Phase 1 — Template & Export Downloads
    // ─────────────────────────────────────────────────────────────────────────

    /**
     * Download a blank Excel template for the given entity type.
     * Calls Spr2.Api (API_URL).
     */
    async downloadTemplate ({ commit, rootState }, entityType) {
      const companyId = rootState.userCurrentCompany.companyId
      const companyName = rootState.userCurrentCompany.companyName
      commit('SET_TEMPLATE_DOWNLOADING', true)
      commit('SET_DOWNLOAD_ERROR', null)
      try {
        const response = await importExportApi.downloadTemplate(companyId, entityType)
        const filename = getFilenameFromResponse(response, buildFilename(companyName, entityType, 'import_template'))
        triggerBlobDownload(new Blob([response.data], {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        }), filename)
      } catch (e) {
        console.error('downloadTemplate failed:', e)
        if (e.response && e.response.status === 401) {
          commit('SET_REQUIRES_REAUTH', true)
          return
        }
        commit('SET_DOWNLOAD_ERROR', `Failed to download template: ${e.message}`)
      } finally {
        commit('SET_TEMPLATE_DOWNLOADING', false)
      }
    },

    /**
     * Download the current-data export for the given entity type.
     * @param {{ entityType: string, options?: { includeArchived?: boolean } }} payload
     */
    async downloadExport ({ commit, rootState }, { entityType, options = {} } = {}) {
      const companyId = rootState.userCurrentCompany.companyId
      const companyName = rootState.userCurrentCompany.companyName
      commit('SET_EXPORT_DOWNLOADING', true)
      commit('SET_DOWNLOAD_ERROR', null)
      try {
        const response = await importExportApi.downloadExport(companyId, entityType, options)
        const filename = getFilenameFromResponse(response, buildFilename(companyName, entityType, 'export'))
        triggerBlobDownload(new Blob([response.data], {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        }), filename)
      } catch (e) {
        console.error('downloadExport failed:', e)
        if (e.response && e.response.status === 401) {
          commit('SET_REQUIRES_REAUTH', true)
          return
        }
        commit('SET_DOWNLOAD_ERROR', `Failed to download export: ${e.message}`)
      } finally {
        commit('SET_EXPORT_DOWNLOADING', false)
      }
    },

    /**
     * Download a zip of all entity type exports.
     * @param {{ includeArchived?: boolean }} options
     */
    async downloadAllExports ({ commit, rootState }, options = {}) {
      const companyId = rootState.userCurrentCompany.companyId
      const companyName = rootState.userCurrentCompany.companyName
      commit('SET_ALL_EXPORTS_DOWNLOADING', true)
      commit('SET_DOWNLOAD_ERROR', null)
      try {
        const response = await importExportApi.downloadAllExports(companyId, options)
        const sanitize = s => (s || '').replace(/[^\w\s-]/g, '').trim().replace(/\s+/g, '_')
        const now = new Date()
        const datePart = `${String(now.getMonth() + 1).padStart(2, '0')}_${String(now.getDate()).padStart(2, '0')}_${now.getFullYear()}`
        const allFallback = companyName ? `${sanitize(companyName)}_all_exports_${datePart}.zip` : 'all-exports.zip'
        const filename = getFilenameFromResponse(response, allFallback)
        triggerBlobDownload(new Blob([response.data], { type: 'application/zip' }), filename)
      } catch (e) {
        console.error('downloadAllExports failed:', e)
        if (e.response && e.response.status === 401) {
          commit('SET_REQUIRES_REAUTH', true)
          return
        }
        commit('SET_DOWNLOAD_ERROR', `Failed to download all exports: ${e.message}`)
      } finally {
        commit('SET_ALL_EXPORTS_DOWNLOADING', false)
      }
    },

    // ─────────────────────────────────────────────────────────────────────────
    // Phase 2 — Validate
    // ─────────────────────────────────────────────────────────────────────────

    async validateFile ({ commit, state, rootState }) {
      const companyId = rootState.userCurrentCompany.companyId
      if (!state.uploadedFile || !state.uploadedFile.entityType) return
      commit('SET_VALIDATING', true)
      commit('SET_VALIDATION_ERROR', null)
      commit('SET_VALIDATION_RESULT', null)
      commit('SET_IMPORT_RESULT', null)
      commit('SET_IMPORT_ERROR', null)
      try {
        const response = await importExportApi.validate(companyId, state.uploadedFile.entityType, state.uploadedFile.file)
        const raw = response.data
        // Normalize API shape → component shape:
        //   validationErrors[] (severity 0=error, 1=warning) → errors[] / warnings[]
        //   annotatedFileBytes → annotatedFileBase64
        let normalizedErrors, normalizedWarnings
        if (Array.isArray(raw.validationErrors)) {
          normalizedErrors = raw.validationErrors.filter(e => e.severity === 'Error' || e.severity === 0 || e.severity === undefined)
          normalizedWarnings = raw.validationErrors.filter(e => e.severity === 'Warning' || e.severity === 1)
        } else {
          normalizedErrors = raw.errors || []
          normalizedWarnings = raw.warnings || []
        }
        const normalized = {
          ...raw,
          errors: normalizedErrors,
          warnings: normalizedWarnings,
          annotatedFileBase64: raw.annotatedFileBase64 || raw.annotatedFileBytes || null
        }
        commit('SET_VALIDATION_RESULT', normalized)
      } catch (e) {
        console.error('validateFile failed:', e)
        const status = e.response && e.response.status
        let message
        if (status === 413) {
          message = 'The file is too large. Maximum file size is 10 MB.'
        } else if (status === 415) {
          message = 'Only Excel (.xlsx) files are supported. Please upload a file with the .xlsx extension.'
        } else if (status === 400) {
          message = 'The file format is invalid or contains structural errors. Use the template above to ensure the correct format.'
        } else {
          message = `Validation request failed: ${e.message}`
        }
        commit('SET_VALIDATION_ERROR', message)
      } finally {
        commit('SET_VALIDATING', false)
      }
    },

    // ─────────────────────────────────────────────────────────────────────────
    // Phase 3 — Import (stub — implementation added in Phase 3)
    // ─────────────────────────────────────────────────────────────────────────

    async importFile ({ commit, state, rootState, dispatch }) {
      const companyId = rootState.userCurrentCompany.companyId
      if (!state.uploadedFile || !state.uploadedFile.entityType) return
      commit('SET_IMPORTING', true)
      commit('SET_IMPORT_ERROR', null)
      commit('SET_IMPORT_RESULT', null)
      try {
        const response = await importExportApi.importData(
          companyId,
          state.uploadedFile.entityType,
          state.uploadedFile.file,
          state.importMode
        )
        // Normalise API response property names to match the Vue template convention.
        // The C# ImportResult serialises as rowsInserted/rowsUpdated/rowsSkipped/rowsErrored/success,
        // while the template reads insertCount/updateCount/skippedCount/isValid/warningRowCount.
        const raw = response.data
        commit('SET_IMPORT_RESULT', {
          ...raw,
          insertCount:     raw.rowsInserted  || 0,
          updateCount:     raw.rowsUpdated   || 0,
          unchangedCount:  raw.rowsUnchanged || 0,
          archivedCount:   raw.rowsArchived  || 0,
          skippedCount:    raw.rowsSkipped   || 0,
          erroredCount:    raw.rowsErrored   || 0,
          isValid:         (raw.errors || []).length === 0 && raw.success !== false,
          warningRowCount: (raw.warnings || []).length
        })
        // Refresh cached data so entity lists reflect the import
        try {
          if (state.uploadedFile.entityType === 'properties') {
            dispatch('company/loadProperties', null, { root: true })
          } else if (state.uploadedFile.entityType === 'roles') {
            dispatch('company/loadPositionTypes', null, { root: true })
          }
        } catch (_) { /* non-critical refresh failure — ignored */ }
      } catch (e) {
        console.error('importFile failed:', e)
        commit('SET_IMPORT_ERROR', `Import request failed: ${e.message}`)
      } finally {
        commit('SET_IMPORTING', false)
      }
    },

    // ─────────────────────────────────────────────────────────────────────────
    // Phase 4+ — Async Job Actions (stubs)
    // ─────────────────────────────────────────────────────────────────────────

    async createExportJob ({ commit, state, rootState }, { jobType, parameters }) {
      const companyId = rootState.userCurrentCompany.companyId
      try {
        const response = await importExportApi.createExportJob(companyId, jobType, parameters)
        const job = {
          jobId: response.data.jobId,
          jobType,
          companyId,
          status: response.data.status,
          downloadUrl: null,
          fileSizeBytes: null,
          expiresAtUtc: null,
          dateRequestedUtc: response.data.dateRequestedUtc || null,
          error: null,
          pollingIntervalId: null
        }
        commit('SET_ACTIVE_JOB', job)
        return job
      } catch (e) {
        // Surface the server's message for 409 Conflict (active job already running).
        const serverMessage = e.response && e.response.data && e.response.data.message
        const err = new Error(serverMessage || e.message)
        err.status = e.response && e.response.status
        console.error('createExportJob failed:', err.message)
        throw err
      }
    },

    async pollJobStatus ({ commit, state }, { jobId, companyId }) {
      try {
        const response = await importExportApi.getExportJobStatus(companyId, jobId)
        const { status, downloadUrl, fileSizeBytes, expiresAtUtc } = response.data
        commit('UPDATE_JOB_STATUS', { jobId, status, downloadUrl, fileSizeBytes, expiresAtUtc, error: null })
        return response.data
      } catch (e) {
        // A failed poll is a transient network/server hiccup — the backend job is still running.
        // Silently skip this tick; the next interval will retry instead of marking the job failed.
        console.warn('pollJobStatus network error (will retry):', jobId, e.message)
      }
    },

    startJobPolling ({ commit, dispatch, state }, { jobId, companyId }) {
      commit('SET_IS_POLLING_JOB', true)
      const intervalId = setInterval(async () => {
        try {
          const result = await dispatch('pollJobStatus', { jobId, companyId })
          if (result && (result.status === 'Complete' || result.status === 'Error' || result.status === 'Expired')) {
            dispatch('stopJobPolling', { jobId })
          }
        } catch (e) {
          // pollJobStatus no longer throws on network errors; this catch is a safety net only.
          console.warn('startJobPolling unexpected error, continuing to poll:', jobId, e.message)
        }
      }, 5000)
      commit('SET_JOB_POLLING_INTERVAL', { jobId, intervalId })
    },

    stopJobPolling ({ commit, state }, { jobId }) {
      const job = state.activeJobs[jobId]
      if (job && job.pollingIntervalId) {
        clearInterval(job.pollingIntervalId)
      }
      commit('SET_IS_POLLING_JOB', false)
    },

    // ─────────────────────────────────────────────────────────────────────────
    // Phase 5 — Events count preview
    // ─────────────────────────────────────────────────────────────────────────

    async loadEventsCount ({ commit, rootState }, filters = {}) {
      const companyId = rootState.userCurrentCompany.companyId
      commit('SET_EVENTS_COUNT_LOADING', true)
      commit('SET_EVENTS_COUNT', null)
      try {
        const response = await importExportApi.getEventsCount(companyId, filters)
        commit('SET_EVENTS_COUNT', response.data.count)
      } catch (e) {
        console.error('loadEventsCount failed:', e)
        commit('SET_EVENTS_COUNT', null)
      } finally {
        commit('SET_EVENTS_COUNT_LOADING', false)
      }
    },

    async downloadCompletedJob ({ commit, state }, { jobId, companyId }) {
      try {
        // Prefer the downloadUrl already stored from the last poll response — avoids a
        // browser-side axios redirect to S3 (which carries auth headers that S3 rejects).
        const stored = state.activeJobs[jobId]
        if (stored && stored.downloadUrl) {
          triggerUrlDownload(stored.downloadUrl)
          return
        }
        // Fallback: ask the API for a fresh presigned URL (returns { url } JSON, not a redirect).
        const response = await importExportApi.getExportJobStatus(companyId, jobId)
        const url = response.data && response.data.downloadUrl
        if (url) {
          // Cache it back into state so next click doesn't need another round-trip.
          commit('UPDATE_JOB_STATUS', {
            jobId,
            status: response.data.status,
            downloadUrl: url,
            fileSizeBytes: response.data.fileSizeBytes || null,
            expiresAtUtc: response.data.expiresAtUtc,
            error: null
          })
          triggerUrlDownload(url)
        } else {
          throw new Error('Download URL is not available for this job.')
        }
      } catch (e) {
        console.error('downloadCompletedJob failed:', e)
        throw e
      }
    },

    async downloadPropertyFiles ({ commit, state, rootState }, { propertyId, options }) {
      const companyId = rootState.userCurrentCompany.companyId
      commit('SET_PROPERTY_FILES_DOWNLOADING', true)
      try {
        const response = await importExportApi.downloadPropertyFiles(companyId, propertyId, options)
        triggerBlobDownload(new Blob([response.data], { type: 'application/zip' }), `property-files-${propertyId}.zip`)
      } catch (e) {
        console.error('downloadPropertyFiles failed:', e)
        throw e
      } finally {
        commit('SET_PROPERTY_FILES_DOWNLOADING', false)
      }
    },

    async getArchiveEstimate ({ commit, state, rootState }) {
      const companyId = rootState.userCurrentCompany.companyId
      commit('SET_ARCHIVE_ESTIMATE_LOADING', true)
      commit('SET_ARCHIVE_ESTIMATE', null)
      try {
        const response = await importExportApi.getArchiveEstimate(companyId)
        commit('SET_ARCHIVE_ESTIMATE', response.data)
        return response.data
      } catch (e) {
        console.error('getArchiveEstimate failed:', e)
        throw e
      } finally {
        commit('SET_ARCHIVE_ESTIMATE_LOADING', false)
      }
    },

    // ─────────────────────────────────────────────────────────────────────────
    // Phase 9 — Load recent export jobs (called on ImportExport page mount)
    // ─────────────────────────────────────────────────────────────────────────

    /**
     * Fetches the user's last 20 export jobs from the API, merges them into
     * activeJobs (so they survive page navigation), and resumes polling for
     * any job still in-flight.
     */
    async loadMyExportJobs ({ commit, dispatch, state, rootState }) {
      const companyId = rootState.userCurrentCompany && rootState.userCurrentCompany.companyId
      if (!companyId) return
      commit('SET_MY_JOBS_LOADING', true)
      try {
        const response = await importExportApi.getExportJobs(companyId)
        const jobs = response.data || []
        for (const apiJob of jobs) {
          const existing = state.activeJobs[apiJob.jobId]
          const merged = {
            jobId:            apiJob.jobId,
            jobType:          apiJob.jobType,
            companyId,
            status:           apiJob.status,
            downloadUrl:      apiJob.downloadUrl || (existing ? existing.downloadUrl : null),
            fileSizeBytes:    apiJob.fileSizeBytes || null,
            expiresAtUtc:     apiJob.expiresAtUtc,
            dateRequestedUtc: apiJob.dateRequestedUtc,
            dateCompletedUtc: apiJob.dateCompletedUtc,
            errorMessage:     apiJob.errorMessage,
            error:            apiJob.errorMessage || null,
            pollingIntervalId: existing ? existing.pollingIntervalId : null
          }
          commit('SET_ACTIVE_JOB', merged)
          // Resume polling for jobs still in flight unless already polling
          if ((merged.status === 'NotStarted' || merged.status === 'InProgress') &&
              !(existing && existing.pollingIntervalId)) {
            dispatch('startJobPolling', { jobId: merged.jobId, companyId })
          }
        }
      } catch (e) {
        console.error('loadMyExportJobs failed:', e)
      } finally {
        commit('SET_MY_JOBS_LOADING', false)
      }
    },

    // ─────────────────────────────────────────────────────────────────────────
    // AI Policy Extraction
    // ─────────────────────────────────────────────────────────────────────────

    /**
     * Upload a PDF/image to the AI-extract endpoint and store the result.
     * The endpoint never saves data — the result is presented to the user for review.
     */
    async extractPolicyFromDocument ({ commit, state, rootState }) {
      if (!state.aiExtractFile) return
      const companyId = rootState.userCurrentCompany.companyId
      commit('SET_AI_EXTRACTING', true)
      commit('SET_AI_EXTRACTION_ERROR', null)
      commit('SET_AI_EXTRACTION_RESULT', null)
      try {
        const response = await importExportApi.aiExtractPolicy(companyId, state.aiExtractFile)
        commit('SET_AI_EXTRACTION_RESULT', response.data)
      } catch (e) {
        console.error('extractPolicyFromDocument failed:', e)
        const status = e.response && e.response.status
        let message
        if (status === 400 && e.response.data && e.response.data.errorMessage) {
          message = e.response.data.errorMessage
        } else if (status === 413) {
          message = 'The document is too large. Maximum file size is 20 MB.'
        } else if (status === 415) {
          message = 'Only PDF and image files are supported.'
        } else if (status === 503) {
          message = 'AI extraction is not currently enabled. Please contact your administrator.'
        } else {
          message = `AI extraction failed: ${e.message}`
        }
        commit('SET_AI_EXTRACTION_ERROR', message)
      } finally {
        commit('SET_AI_EXTRACTING', false)
      }
    }
  }
}



// WEBPACK FOOTER //
// ./src/store/modules/importExport.js